import { OfferFormValues } from '../../schemas/offerFormSchema';
import { Offer } from '@/types/offer';
import { ensureValidDate } from '@/utils/dateUtils';
import { isCampDataOffer, SESSION_FACT_FIELDS } from '@/functions-shared/offer/storedSessionPrice';

export const isNumDaysWithheld = (offer?: Offer | null): boolean =>
  (offer as any)?.numDaysState === "withheld";

// KB-4888 — the length a camp-data session itself stores. Only there does the save keep it (KB-3733: the
// dialog never sends it); a hand-curated session's length is derived from its override or its program on
// every save, so showing its stored count would show a number the save then rewrites. It is the stored
// length, not a verified count of camp days: for an adapter that supplies no `numDays` it is an inclusive
// calendar span (offerComputeCore.cjs, "NOT A COUNT OF CAMP DAYS"). The sessions table already shows the
// same stored value. A withheld session has none (data-model §1d), so it never reads as a count.
export const getSessionOwnNumDays = (offer?: Offer | null): number | null => {
  if (!isCampDataOffer(offer as unknown as Record<string, unknown> | null) || isNumDaysWithheld(offer)) return null;
  const n = offer?.numDaysCalc;
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? n : null;
};

export const prepareSubmissionValues = (
  values: OfferFormValues,
  initialOffer?: Offer | null,
  isEqualToProgramValue?: (fieldName: string, value: any) => boolean
) => {
  // Clone the values to avoid modifying the original
  const submissionValues = { ...values };
  const variation = submissionValues.variation;

  const normalizeTierPrice = (n: any): number | null => {
    return typeof n === "number" && !Number.isNaN(n) ? n : null;
  };

  // KB-3733 — on a camp-data session the baseline is the session's OWN price and length, not the
  // program's, so a price or tier override that equals the program value is a real override (an approved
  // $275 off an observed $300, or a curator typing the program price in; the form pre-fills price only for
  // NEW offers, useFormInitialization). The "equal to program → redundant" clearing is rule-2 logic and
  // does not run for price/tier on a camp-data session.
  //
  // numDaysOverride is the exception: TimingSection PRE-FILLS it whenever the stored value is null, for
  // existing offers too. The prefill is the session's own numDaysCalc, or the program's days when the
  // session has none, so an equal-to-program length there is usually the prefill, not intent. It clears
  // unless it is the stored value (see below).
  const programIsBaseline = !isCampDataOffer(initialOffer as unknown as Record<string, unknown> | null);
  const clearsAsProgramValue = (field: string, value: unknown): boolean =>
    programIsBaseline && !!isEqualToProgramValue && isEqualToProgramValue(field, value);

  // If tier overrides match the effective program value (base or alternative), null them out
  // so we don't persist redundant overrides, and "clear" works predictably.
  if (isEqualToProgramValue && (submissionValues as any).pricingTierPriceOverrides != null) {
    const pt: any = (submissionValues as any).pricingTierPriceOverrides;
    const t2 = normalizeTierPrice(pt?.tier2);
    const t3 = normalizeTierPrice(pt?.tier3);
    if (clearsAsProgramValue("pricingTierPriceOverrides.tier2", t2)) {
      pt.tier2 = null;
    }
    if (clearsAsProgramValue("pricingTierPriceOverrides.tier3", t3)) {
      pt.tier3 = null;
    }
  }

  // Normalize pricingTierPriceOverrides (map field). Keep null when empty/off.
  if ((submissionValues as any).pricingTierPriceOverrides == null) {
    (submissionValues as any).pricingTierPriceOverrides = null;
  } else {
    const pt: any = (submissionValues as any).pricingTierPriceOverrides;
    const tier2 = normalizeTierPrice(pt?.tier2);
    const tier3 = normalizeTierPrice(pt?.tier3);
    const out: any = { v: 1 };
    if (tier2 !== null) out.tier2 = tier2;
    if (tier3 !== null) out.tier3 = tier3;
    (submissionValues as any).pricingTierPriceOverrides =
      Object.keys(out).length > 1 ? out : null;
  }

  // FIXED: Set priceRegular based on priceOverride or variation.priceRegular
  if (!programIsBaseline) {
    // KB-3733 — a camp-data session's list price is its own and is resolved from the FRESH stored doc at
    // save time; the session-fact fields are stripped from this submission below, never sent.
  } else if (variation) {
    if (submissionValues.priceOverride !== null && submissionValues.priceOverride !== undefined) {
      submissionValues.priceRegular = submissionValues.priceOverride;
    } else {
      submissionValues.priceRegular = variation.priceRegular;
    }
  }
  
  // ENHANCED: Check if price override matches program price OR alternative pricing
  if (clearsAsProgramValue("priceOverride", submissionValues.priceOverride)) {
    submissionValues.priceOverride = null;
  } else if (programIsBaseline && variation && submissionValues.priceOverride === variation.priceRegular) {
    // Fallback to base comparison if isEqualToProgramValue is not available
    submissionValues.priceOverride = null;
  }
  
  // Calculate priceCalc based on currentSalePrice > priceOverride > priceRegular
  const calculatedPrice = submissionValues.currentSalePrice !== null && submissionValues.currentSalePrice !== undefined
    ? submissionValues.currentSalePrice
    : submissionValues.priceOverride !== null && submissionValues.priceOverride !== undefined
      ? submissionValues.priceOverride
      : submissionValues.priceRegular;
  
  // CRITICAL FIX: Use isEqualToProgramValue for footnote fields to handle alternative pricing
  if (isEqualToProgramValue) {
    // For priceNoteOverride: use the enhanced function that checks base AND alternative pricing
    if (isEqualToProgramValue("priceNoteOverride", submissionValues.priceNoteOverride)) {
      submissionValues.priceNoteOverride = null;
    }
    
    // For timingNoteOverride: use the enhanced function 
    if (isEqualToProgramValue("timingNoteOverride", submissionValues.timingNoteOverride)) {
      submissionValues.timingNoteOverride = null;
    }
  } else {
    // Fallback to base comparison if isEqualToProgramValue is not available
    const programPriceNote = variation?.priceNote || "";
    const programTimingNote = variation?.timings?.timingNote || "";
    
    if (submissionValues.priceNoteOverride === programPriceNote) {
      submissionValues.priceNoteOverride = null;
    }
    
    if (submissionValues.timingNoteOverride === programTimingNote) {
      submissionValues.timingNoteOverride = null;
    }
  }
  
  // NEW: Handle myOfferIncrementKey - only save if session length selector would be visible
  // Session length selector is shown when program has multiple increment options total (default + alternatives)
  let shouldSaveIncrementKey = false;
  if (variation) {
    // Count total increment options: default + alternatives (matching UI logic)
    let totalOptions = 0;
    
    // Count default option
    if (variation.regularOfferIncrementKey) {
      totalOptions += 1;
    }
    
    // Count alternative options
    if (variation.regularOfferIncrementAlternatives && variation.regularOfferIncrementAlternatives.length > 0) {
      totalOptions += variation.regularOfferIncrementAlternatives.length;
    }
    
    // Session length selector is visible when there are multiple options (matching UI logic)
    shouldSaveIncrementKey = totalOptions > 1;
    
  }
  
  // Create a final submission object that includes our calculated fields
  const finalSubmission: any = {
    ...submissionValues,
    priceCalc: calculatedPrice
  };
  
  // KB-2877 (review round 1, B4) — a WITHHELD length must survive a curator's unrelated edit.
  //
  // This read `initialOffer?.numDaysCalc || (variation?.numDays || 1)`, and `null` is falsy, so
  // opening a withheld session to change something else entirely — a timing note — re-substituted
  // the variation default and republished `numDaysCalc: 28` with `pricePerDayCalc = price / 28`.
  // The refusal was undone by a curator who never touched the field.
  //
  // `??` alone would NOT fix this: the fallback itself is the defect for this class, so the
  // withheld state has to gate it. `numDaysState` is the stored companion that says WHY the value
  // is null (data-model §1d) — `numDaysCalc: null` on its own cannot distinguish a deliberate
  // withhold from a legacy row that never carried a count.
  const isWithheld = (initialOffer as any)?.numDaysState === "withheld";
  const numDaysCalc = isWithheld ? null : initialOffer?.numDaysCalc || (variation?.numDays || 1);

  finalSubmission.numDaysCalc = numDaysCalc;
  if (isWithheld) finalSubmission.numDaysState = "withheld";

  // Calculate pricePerDayCalc if we have numDaysCalc and priceCalc. A withheld length nulls this
  // WITH it — the price-shaped fields normalise together (data-model §1c/§1d), or the offer keeps
  // answering the per-day filter with a fabricated figure.
  if (isWithheld) {
    finalSubmission.pricePerDayCalc = null;
  } else if (calculatedPrice !== undefined && numDaysCalc && numDaysCalc > 0) {
    finalSubmission.pricePerDayCalc = calculatedPrice / numDaysCalc;
  }
  
  // For numDaysOverride
  const numDaysEqualsProgram = !!isEqualToProgramValue && isEqualToProgramValue("numDaysOverride", submissionValues.numDaysOverride);
  const storedNumDaysOverride = (initialOffer as any)?.numDaysOverride ?? null;
  // KB-4888 — TimingSection pre-fills a camp-data session that has no override with the session's OWN
  // length, so that number coming back unchanged is the prefill, not intent. It would otherwise land as an
  // override whenever the session's length differs from the program's.
  const numDaysIsSessionPrefill =
    storedNumDaysOverride === null &&
    getSessionOwnNumDays(initialOffer) !== null &&
    submissionValues.numDaysOverride === getSessionOwnNumDays(initialOffer);
  if (
    (numDaysEqualsProgram && (programIsBaseline || submissionValues.numDaysOverride !== storedNumDaysOverride)) ||
    numDaysIsSessionPrefill
  ) {
    finalSubmission.numDaysOverride = null;
  }

  // NEW: Age overrides — map to agesAreOverrides (DB flag)
  finalSubmission.agesAreOverrides = !!submissionValues.overrideAges;

  // Build ephemeral payload for offerDataPreparation to compute effective variationData ages
  if (submissionValues.overrideAges) {
    (finalSubmission as any).variationAgeOverrideInput = {
      minAgeInYears: submissionValues.minAgeInYearsOverride ?? null,
      maxAgeInYears: submissionValues.maxAgeInYearsOverride ?? null,
      minAgeGradeKey: submissionValues.minAgeGradeKeyOverride || null,
      maxAgeGradeKey: submissionValues.maxAgeGradeKeyOverride || null,
      minAgeNote: submissionValues.minAgeNoteOverride ?? "",
      maxAgeNote: submissionValues.maxAgeNoteOverride ?? "",
      showMinAgeGradeAsPrimary: !!submissionValues.showMinAgeGradeAsPrimaryOverride,
      showMaxAgeGradeAsPrimary: !!submissionValues.showMaxAgeGradeAsPrimaryOverride
    };
  } else {
    delete (finalSubmission as any).variationAgeOverrideInput;
  }

  // Strip UI-only override fields from submission
  delete (finalSubmission as any).minAgeInYearsOverride;
  delete (finalSubmission as any).maxAgeInYearsOverride;
  delete (finalSubmission as any).minAgeGradeKeyOverride;
  delete (finalSubmission as any).maxAgeGradeKeyOverride;
  delete (finalSubmission as any).minAgeNoteOverride;
  delete (finalSubmission as any).maxAgeNoteOverride;
  delete (finalSubmission as any).showMinAgeGradeAsPrimaryOverride;
  delete (finalSubmission as any).showMaxAgeGradeAsPrimaryOverride;
  delete (finalSubmission as any).overrideAges;
  
  // CRITICAL FIX: Filter out empty registration events (those without dates)
  if (submissionValues.regEvents) {
    
    // Filter out events that don't have valid dates
    const validEvents = submissionValues.regEvents.filter(event => 
      event.when !== undefined && 
      event.when !== null && 
      (event.when instanceof Date ? !isNaN(event.when.getTime()) : true)
    );
    
    
    // If no valid events remain, set to null (don't save empty arrays)
    if (validEvents.length === 0) {
      finalSubmission.regEvents = null;
    } else {
      finalSubmission.regEvents = validEvents;
    }
  } else {
    finalSubmission.regEvents = null;
  }
  
  // Ensure dates are valid before submitting
  const safeValues = {
    ...finalSubmission,
    startDate: ensureValidDate(values.startDate),
    endDate: ensureValidDate(values.endDate),
    visibility: "published" // Always set visibility to "published"
  };
  
  // NEW: Conditionally include myOfferIncrementKey based on whether session length selector is visible
  if (shouldSaveIncrementKey && submissionValues.myOfferIncrementKey) {
    safeValues.myOfferIncrementKey = submissionValues.myOfferIncrementKey;
  } else {
    // Explicitly exclude myOfferIncrementKey when session length selector is not visible
    // or when no increment key is set
    delete safeValues.myOfferIncrementKey;
  }

  // KB-3733 — a camp-data session's facts (list price, length, tier prices, price state) are NOT the
  // dialog's to send. Its values are a snapshot from when it opened — sending them would revert a price an
  // Inbox approve wrote meanwhile — and `numDaysCalc` above is a catalog fallback for a null length.
  // updateOffer honours these keys only when a caller sets them on purpose (a history restore). The
  // price/per-day figures computed above are dropped with them: the save path derives both.
  if (!programIsBaseline) {
    for (const k of SESSION_FACT_FIELDS) delete (safeValues as any)[k];
    delete (safeValues as any).priceCalc;
    delete (safeValues as any).pricePerDayCalc;
  }

  return safeValues;
};
